import { type CaptionPosition, type CaptionStyle, drawCaption } from '@reelplicate/flows';
import { cn } from '@reelplicate/ui';
import { useEffect, useRef, useState } from 'react';

import { useCaptionFonts } from './caption-fonts';
import { SAMPLE_CAPTION } from './caption-style-options';

/**
 * A slide with its caption drawn in a style: the same drawing the renderer
 * uses, on a canvas. Shows the image when given one (cropped to fill a 4:5
 * slide), else a warm neutral backdrop.
 */
export function CaptionPreview({
  style,
  text = SAMPLE_CAPTION,
  position = 'center',
  imageUrl,
  width: fixedWidth,
  label,
  className,
}: {
  style: CaptionStyle;
  text?: string;
  position?: CaptionPosition;
  imageUrl?: string | null;
  /** CSS width in px; the height follows at 4:5. Omit to fill the container. */
  width?: number;
  label: string;
  className?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const fonts = useCaptionFonts();
  const [loaded, setLoaded] = useState<{ url: string; img: HTMLImageElement } | null>(
    null,
  );
  const image = imageUrl && loaded?.url === imageUrl ? loaded.img : null;
  const [measured, setMeasured] = useState(0);
  const cssWidth = fixedWidth ?? measured;
  const height = Math.round(cssWidth * 1.25);

  useEffect(() => {
    const el = canvas.current;
    if (fixedWidth !== undefined || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setMeasured(Math.round(el.clientWidth)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [fixedWidth]);

  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.onload = () => setLoaded({ url: imageUrl, img });
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext?.('2d');
    if (!el || !ctx || !cssWidth) return;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    el.width = Math.round(cssWidth * scale);
    el.height = Math.round(height * scale);
    const w = el.width;
    const h = el.height;
    if (image) {
      const cover = Math.max(w / image.width, h / image.height);
      const iw = image.width * cover;
      const ih = image.height * cover;
      ctx.drawImage(image, (w - iw) / 2, (h - ih) / 2, iw, ih);
    } else {
      const backdrop = ctx.createLinearGradient(0, 0, 0, h);
      backdrop.addColorStop(0, '#6d7f95');
      backdrop.addColorStop(0.5, '#b8876a');
      backdrop.addColorStop(1, '#3a2620');
      ctx.fillStyle = backdrop;
      ctx.fillRect(0, 0, w, h);
    }
    drawCaption(ctx, {
      width: w,
      height: h,
      text,
      position,
      style,
    });
  }, [style, text, position, image, cssWidth, height, fonts]);

  return (
    <canvas
      ref={canvas}
      role="img"
      aria-label={label}
      style={fixedWidth === undefined ? undefined : { width: fixedWidth, height }}
      className={cn(
        'bg-bg-muted block rounded-md',
        fixedWidth === undefined && 'aspect-[4/5] w-full',
        className,
      )}
    />
  );
}
